'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Upload,
  BarChart2,
  Building2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
}

const navItems: NavItem[] = [
  {
    id: 'nav-import',
    label: 'Importar Dados',
    href: '/',
    icon: <Upload size={20} />,
  },
  {
    id: 'nav-report',
    label: 'Relatório Comparativo',
    href: '/comparison-report',
    icon: <BarChart2 size={20} />,
  },
  {
    id: 'nav-institution',
    label: 'Detalhe por Instituição',
    href: '/institution-detail',
    icon: <Building2 size={20} />,
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`
        relative flex flex-col bg-card border-r border-border
        sidebar-transition shrink-0
        ${collapsed ? 'w-16' : 'w-60'}
      `}
      style={{ minHeight: '100vh' }}
    >
      {/* Logo */}
      <div className={`flex items-center border-b border-border px-4 py-4 ${collapsed ? 'justify-center' : 'justify-center'}`}>
        {!collapsed && (
          <div className="flex items-center justify-center">
            <img
              src="/assets/images/banco_portugal-1784204978793.png"
              alt="Banco de Portugal"
              className="h-24 object-contain"
            />
          </div>
        )}
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-16 z-10 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-all duration-150 shadow-sm"
        aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* Nav section label */}
      {!collapsed && (
        <div className="px-4 pt-5 pb-1">
          <span className="text-[11px] font-500 tracking-widest uppercase text-muted-foreground">Análise</span>
        </div>
      )}

      {/* Main nav items */}
      <nav className="flex-1 px-2 pt-2 pb-4 space-y-0.5">
        {navItems.map((item) => {
          const isActive = pathname === item.href;
          return (
            <Link
              key={item.id}
              href={item.href}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg
                transition-all duration-150 group relative
                ${collapsed ? 'justify-center' : ''}
                ${isActive
                  ? 'bg-primary/10 text-primary font-medium' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                }
              `}
              title={collapsed ? item.label : undefined}
            >
              <span className={`shrink-0 ${isActive ? 'text-primary' : ''}`}>{item.icon}</span>
              {!collapsed && (
                <span className="text-sm truncate">{item.label}</span>
              )}
              {!collapsed && item.badge && (
                <span className="ml-auto text-[11px] font-600 bg-accent text-accent-foreground rounded-full px-1.5 py-0.5 leading-none">
                  {item.badge}
                </span>
              )}
              {collapsed && (
                <span className="
                  absolute left-full ml-2 px-2 py-1 bg-foreground text-background
                  text-xs rounded whitespace-nowrap opacity-0 pointer-events-none
                  group-hover:opacity-100 transition-opacity duration-150 z-50
                ">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}